import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type ChanneltalkInfo, IMPORT_STATE } from '../types';

export const useChanneltalk = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.CHANNELTALK,
    workspace_alias,
  );

  const { data } = useQuery({
    queryKey: queryKeys,
    queryFn: () => service.channeltalk.getChannelInfo(workspace_alias),
    enabled: enabled,
  });

  const { mutate: disconnectMutation } = useMutation({
    mutationFn: (channelAlias: string) =>
      service.channeltalk.deleteChannelInfo(channelAlias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: ({
      channelAlias,
      dateRange,
    }: {
      channelAlias: string;
      dateRange: NullableDateRangeT;
    }) => service.channeltalk.importChannelFeedback(channelAlias, dateRange),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        queryKeys,
        (old: ChanneltalkInfo[] | undefined) => {
          if (!old) return old;

          return old.map((channel) =>
            channel.alias === variables.channelAlias
              ? { ...channel, import_state: IMPORT_STATE.IMPORTING }
              : channel,
          );
        },
      );
    },
  });

  return {
    workspaces: data,
    disconnectMutation,
    requestImportMutation,
  };
};
